import 'grapesjs-blocks-basic';
import mapValues from 'lodash/mapValues';
import merge from 'lodash/merge';
import grapesjs, { Editor, EventCallback } from 'grapesjs';
import {
  CurrencyAttributes,
  ReferralConfigAttributes,
} from '@loyalty/shared-common/gamification-api-client';
import { DEFAULT_LANGUAGE, FeatureMapping } from '@loyalty/shared-common';
import { DialogProps } from '@mui/material';
import {
  EditorTheme,
  FontAttributes,
  GoogleFontSettings,
  TemplateCustomCode,
} from '@loyalty/shared-common/template-api-client';
import { ReactNode } from 'react';
import {
  STRING_NOT_TRANSLATED,
  participantTranslations,
  translations,
} from '@loyalty/translations';
import { TFunction, i18n } from 'i18next';
import { WidgetState, convertThemeToCss, getFontFace } from '@loyalty/editor-components';
import { uploadFileValidation } from '@loyalty/shared-common/asset-api-client';
import {
  ACTION_PANEL_ID,
  BLOCKS_ID,
  CANVAS_ID,
  DEVICES_PANEL_ID,
  STYLES_ID,
  TRAITS_ID,
} from '../config';
import { ASSETS_HASH } from '../../shared/assets-hash';
import { ApiClientAttribute } from '../../shared/api-client/ApiClientProvider';
import { EditorModalContentProps } from '../components/EditorModal';
import { PreviewCommand, SetDeviceCommand } from '../editor/commands';
import { editorComponentsDefinitions } from '../editor/components/components';
import { flattenTree } from './flatten-components';
import { getBlockLabel } from '../editor/components/utils/blocks';
import { injectCss } from './inject-css';
import { injectQuillFontsStyles } from './quill-fonts';
import { rtePlugin } from './rte-plugin';
import { sanitizeEditorComponents } from '../helpers/sanitize-editor-canvas-html';

const NAV_COMPONENT_ID = 'navigations';

export interface EditorServiceOptions {
  apis: ApiClientAttribute;
  schema?: Record<string, unknown>[];
  content?: string;
  languages?: string[];
  i18n: i18n;
  t: TFunction;
  programId: string;
  participantUrl: string;
  customCode?: TemplateCustomCode;
  styles?: string;
  features: FeatureMapping<boolean>;
}

export interface EditorModalContent {
  content: ReactNode;
  dialogProps?: Partial<DialogProps>;
}

export interface EditorContext {
  qualifioApiUrl: string;
  accountId: string;
  programId: string;
  languages: string[];
  initialLocale: string;
  referralConfig?: ReferralConfigAttributes;
  currencies: CurrencyAttributes[];
  [key: string]: unknown;
  openModal: (getContent: (props: EditorModalContentProps) => EditorModalContent) => void;
  apis: ApiClientAttribute;
  theme?: EditorTheme;
  fonts?: FontAttributes[];
  t?: TFunction;
  widgetState: WidgetState;
}

const selectors = {
  container: `#${CANVAS_ID}`,
  blocks: `#${BLOCKS_ID}`,
  actions: `#${ACTION_PANEL_ID}`,
  devices: `#${DEVICES_PANEL_ID}`,
  styles: `#${STYLES_ID}`,
  traits: `#${TRAITS_ID}`,
};

export class EditorService {
  public editor!: Editor;

  constructor(private options: EditorServiceOptions, private context?: EditorContext) {
    this.initialize();
    this.initializeBlocks();
    this.initializeCommands();
  }

  initialize() {
    const { i18n, apis } = this.options;

    const editorTranslations = mapValues(translations, languageTranslations =>
      mapValues(languageTranslations, (value, key) =>
        value && value !== STRING_NOT_TRANSLATED
          ? value
          : (translations as { en: Record<string, string> })['en'][key],
      ),
    );

    const locale = this.context?.initialLocale;

    this.editor = grapesjs.init({
      storageManager: {
        type: 'local',
        autoload: false,
        autosave: false,
      },
      container: selectors.container,
      canvas: {
        styles: [
          `${BASE_PATH}/assets/template/assets/fontawesome/css/all.min.css?version=${ASSETS_HASH}`,
          `${BASE_PATH}/assets/template/index.css?hash=${ASSETS_HASH}`, // main member portal styles (see in template-assets)
          `${BASE_PATH}/assets/template/assets/quill/quill.snow.css?hash=${ASSETS_HASH}`, // QuillJS styles for Rich Text Editor in Grapesjs
        ],
        scripts: [
          `${BASE_PATH}/assets/template/index.js?hash=${ASSETS_HASH}`, // main JS for member portal (see in template-assets)
          `${BASE_PATH}/assets/template/assets/quill/quill.min.js?hash=${ASSETS_HASH}`, // QuillJS lib so that editor.Canvas.getWindow().Quill is defined
        ],
      },
      components: sanitizeEditorComponents(this.options.schema),
      // TODO remove regex in a couple of weeks
      style: this.options.styles?.replace(/:root\{[^}]*}/i, '') || '',
      panels: {
        defaults: [],
      },
      selectorManager: {
        appendTo: selectors.styles,
        componentFirst: true,
      },
      styleManager: {
        appendTo: selectors.styles,
        sectors: [
          {
            name: 'General',
            open: false,
            buildProps: [
              'float',
              'display',
              'flex-direction',
              'justify-content',
              'align-items',
              'flex-grow',
              'flex-shrink',
              'position',
              'top',
              'bottom',
              'left',
              'right',
              'background-color',
              'border-radius',
            ],
          },
          {
            name: 'Dimension',
            open: false,
            buildProps: ['width', 'height', 'max-width', 'max-height', 'min-width', 'min-height'],
          },
          {
            name: 'Margin',
            open: false,
            buildProps: ['margin-top', 'margin-bottom', 'margin-left', 'margin-right'],
          },
          {
            name: 'Padding',
            open: false,
            buildProps: ['padding-top', 'padding-bottom', 'padding-left', 'padding-right'],
          },
          {
            name: 'Typography',
            open: false,
            buildProps: [
              'text-align',
              'font-size',
              'font-weight',
              'letter-spacing',
              'line-height',
              'color',
            ],
          },
        ],
      },
      blockManager: {
        appendTo: selectors.blocks,
      },
      traitManager: {
        appendTo: selectors.traits,
      },
      deviceManager: {
        appendTo: selectors.actions,
      },
      assetManager: {
        assets: [],
        upload: true,
        multiUpload: false,
        uploadFile: async function (this: any, e: any) {
          const editor = this.config.em.getEditor();

          const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
          const file = files?.[0];

          if (!file) {
            return;
          }

          const [isValid, error] = await uploadFileValidation(file, i18n);
          if (!isValid) {
            editor.trigger('asset:upload:error', error);
            return;
          }

          return apis.assetApi
            .uploadFile(editor.Config.context.programId, 'editor', file)
            .then(({ url }) => {
              editor.AssetManager.add(url);
            })
            .catch(err => editor.trigger('asset:upload:error', err.message));
        },
      },
      plugins: [
        rtePlugin,
        ...editorComponentsDefinitions.filter(c => !!c.component).map(({ component }) => component),
      ],
      i18n: {
        locale,
        localeFallback: 'en',
        locales: this.context?.languages,
        detectLocale: false,
        messages: merge({}, editorTranslations, participantTranslations),
      },
      context: this.context,
    });

    this.editor.on('load update', result => {
      const componentTypes = new Set(
        flattenTree({ components: this.editor.getComponents() })
          .map(c => c.type)
          .filter(v => !!v),
      );
      (this.editor as any)._types = componentTypes;
      (this.editor.BlockManager.get(NAV_COMPONENT_ID) as any).attributes.disable =
        componentTypes.has(NAV_COMPONENT_ID);
      this.editor.BlockManager.render();
    });

    this.editor.on('component:update:classes', editor => {
      const selectedComponent = this.editor.getSelected();
      selectedComponent?.view.onRender();
    });

    this.editor.on('load', () => {
      this.injectQuillStyles();
      this.injectTheme(this.context?.theme);
      this.injectCustomCss(this.options.customCode?.css);
      this.editor.Canvas.getDocument().documentElement.lang = locale || DEFAULT_LANGUAGE;
    });

    this.editor.on('i18n:locale', () => {
      // need to wait for next tick to have the updated locale, otherwise the re-render happens with the previous locale
      setTimeout(() => {
        const newLocale = this.editor.I18n.getLocale();
        this.editor.Canvas.getDocument().documentElement.lang = newLocale;

        this.editor.getComponents().forEach((component: any) => {
          component.view?.render();
        });
      });
    });

    this.editor.on('component:remove', (component: any) => {
      const win = this.editor.Canvas.getWindow() as any;
      if (
        win &&
        win._async_bind_react_widget &&
        typeof win._async_bind_react_widget.unmount === 'function'
      ) {
        win._async_bind_react_widget.unmount(component.getId());
      }
    });
  }

  destroy() {
    this.editor.destroy();
  }

  on(eventName: string, callback: EventCallback) {
    this.editor.on(eventName, callback);
  }

  off(eventName: string, callback: EventCallback) {
    this.editor.off(eventName, callback);
  }

  setTheme(theme: EditorTheme) {
    if (this.context) {
      this.context.theme = theme;
      this.injectTheme(theme);
    }
  }

  setFonts(fonts: FontAttributes[]) {
    if (this.context) {
      this.context.fonts = fonts;

      // setTimeout introduced for Firefox tick issue where this.editor.Canvas is undefined on init
      setTimeout(() => {
        const head = this.editor.Canvas.getDocument().head;
        head.querySelectorAll('[data-font]').forEach(el => el.remove());
        fonts
          .filter(font => font.type === 'google')
          .forEach(font => {
            const link = document.createElement('link');
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('href', (font.settings as GoogleFontSettings).link);
            link.setAttribute('data-font', font.name);
            head.appendChild(link);
          });
        fonts
          .filter(font => font.type === 'custom')
          .forEach(font => {
            const link = document.createElement('style');
            link.setAttribute('data-font', font.name);
            link.innerHTML = getFontFace(font);
            head.appendChild(link);
          });

        this.injectQuillStyles();
      }, 0);
    }
  }

  setCustomCode(customCode: TemplateCustomCode): void {
    this.options.customCode = customCode;
    if (customCode.css) {
      this.injectCustomCss(customCode.css);
    }
  }

  getStyles() {
    return this.editor.getCss({ avoidProtected: true });
  }

  setComponents(schema: unknown, styles: string) {
    this.editor.setComponents(sanitizeEditorComponents(schema));
    this.editor.setStyle(styles);
  }

  private initializeBlocks() {
    const blockManager = this.editor.BlockManager;
    const { t, features } = this.options;

    const blockDefinitions = editorComponentsDefinitions.filter(
      ({ category, hidden, isEnabled }) => !!category && !hidden && (isEnabled?.(features) ?? true),
    );
    for (const definition of blockDefinitions) {
      blockManager.remove(definition.id); // ensures our blocks overwrite any block added by a plugin (e.g. tabs)
      blockManager.add(definition.id, {
        category: definition.category,
        label: getBlockLabel(definition.getLabel({ t }), definition.icon, { classes: 'fa-3x' }),
        content: {
          type: definition.id,
          content: t('editor.text.default'),
        },
      });
    }

    return this;
  }

  private initializeCommands() {
    this.editor.runCommand('sw-visibility');
    this.editor.Commands.extend('preview', PreviewCommand);
    this.editor.Commands.add('set-device', SetDeviceCommand);
  }

  private injectQuillStyles() {
    if (this.context?.fonts) {
      injectQuillFontsStyles(this.editor, this.context.fonts);
    }
  }

  private injectTheme(theme?: EditorTheme) {
    injectCss(this.editor, 'qlf-theme', theme ? convertThemeToCss(theme) : '');
  }

  private injectCustomCss(css?: string) {
    injectCss(this.editor, 'qlf-custom-css', css || '');
  }
}
